/**
 * Steuerung — einhändig im Hochformat, Tastatur am Schreibtisch.
 *
 * **Zwei Regeln, aus denen die Fehler des ersten Spieltests folgten:**
 *
 * 1. Die Steuerung horcht **ausschließlich auf der Spielfläche** (`#brett`),
 *    nie auf dem ganzen Bildschirm. Vorher fing sie mit `setPointerCapture`
 *    jeden Zeiger in der unteren Bildschirmhälfte ab — auch den auf dem
 *    Absteigen-Knopf, der dadurch nichts tat. Was Oberfläche ist, ist nie
 *    Steuerfläche (Ueberarbeitung A1).
 *
 * 2. Die gewollte Laufrichtung hat **genau einen Besitzer**: die Simulation.
 *    Diese Klasse meldet nur, was der Spieler in diesem Takt WILL. Vorher hielt
 *    sie eine zweite Richtung, die auseinanderlief — Auto-Lauf ließ sich nicht
 *    beenden, und WASD wirkte nur, solange die Taste gedrückt war
 *    (Ueberarbeitung A2).
 */

import type { Absicht, Automatikgrad, Vek } from '@pg/protocol';

export interface Stickzustand {
  aktiv: boolean;
  ursprung: Vek;
  jetzt: Vek;
}

const TOTZONE = 12;
export const STICK_RADIUS = 52;

export class Steuerung {
  #stick: Stickzustand = { aktiv: false, ursprung: { x: 0, y: 0 }, jetzt: { x: 0, y: 0 } };
  #zeiger: number | null = null;
  /** Richtung aus dem Daumen. Null, sobald der Finger weg ist. */
  #daumen: Vek | null = null;
  #tasten = new Set<string>();
  #wartende: Absicht[] = [];
  #flaeche: HTMLElement;
  /** Zwei-Finger-Zoom: Abstand beim Aufsetzen. */
  #zoomStart: number | null = null;
  #beiZoom: ((raus: boolean) => void) | null = null;
  #beiTipp: ((x: number, y: number) => void) | null = null;
  #tippStart = 0;
  #tippPos: Vek = { x: 0, y: 0 };

  constructor(flaeche: HTMLElement) {
    this.#flaeche = flaeche;
    this.#hoereZu();
  }

  get stick(): Stickzustand {
    return this.#stick;
  }

  /** Rückruf für Zwei-Finger-Zoom und Mausrad. */
  beiZoom(f: (raus: boolean) => void): void {
    this.#beiZoom = f;
  }

  /**
   * Rückruf für einen kurzen Tipp ohne Ziehen — damit ist Beute anklickbar,
   * ohne dass die Figur dabei losläuft.
   */
  beiTipp(f: (x: number, y: number) => void): void {
    this.#beiTipp = f;
  }

  #hoereZu(): void {
    const f = this.#flaeche;
    const aktiv = new Map<number, { x: number; y: number }>();

    f.addEventListener('pointerdown', (e) => {
      aktiv.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (aktiv.size === 2) {
        // Zweiter Finger: Zoom statt Bewegung.
        const [a, b] = [...aktiv.values()];
        this.#zoomStart = Math.hypot(a!.x - b!.x, a!.y - b!.y);
        this.#zeiger = null;
        this.#stick.aktiv = false;
        this.#daumen = null;
        return;
      }
      if (aktiv.size > 2) return;
      if (this.#zeiger !== null) return;

      this.#zeiger = e.pointerId;
      this.#tippStart = e.timeStamp;
      this.#tippPos = { x: e.clientX, y: e.clientY };
      f.setPointerCapture(e.pointerId);
      this.#stick = {
        aktiv: true,
        ursprung: { x: e.clientX, y: e.clientY },
        jetzt: { x: e.clientX, y: e.clientY },
      };
    });

    f.addEventListener('pointermove', (e) => {
      if (aktiv.has(e.pointerId)) aktiv.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (this.#zoomStart !== null && aktiv.size === 2) {
        const [a, b] = [...aktiv.values()];
        const jetzt = Math.hypot(a!.x - b!.x, a!.y - b!.y);
        if (Math.abs(jetzt - this.#zoomStart) > 45) {
          this.#beiZoom?.(jetzt < this.#zoomStart);
          this.#zoomStart = jetzt;
        }
        return;
      }

      if (e.pointerId !== this.#zeiger) return;
      this.#stick.jetzt = { x: e.clientX, y: e.clientY };
      const dx = this.#stick.jetzt.x - this.#stick.ursprung.x;
      const dy = this.#stick.jetzt.y - this.#stick.ursprung.y;
      const l = Math.hypot(dx, dy);
      this.#daumen = l < TOTZONE ? null : { x: dx / l, y: dy / l };
    });

    const loslassen = (e: PointerEvent): void => {
      aktiv.delete(e.pointerId);
      if (aktiv.size < 2) this.#zoomStart = null;
      if (e.pointerId !== this.#zeiger) return;

      // Kurz und ohne Ziehen? Dann war es ein Klick, keine Steuerung.
      const gezogen = Math.hypot(e.clientX - this.#tippPos.x, e.clientY - this.#tippPos.y);
      if (e.timeStamp - this.#tippStart < 280 && gezogen < 14) {
        this.#beiTipp?.(e.clientX, e.clientY);
      }

      this.#zeiger = null;
      this.#stick.aktiv = false;
      // Der Daumen ist weg — also will der Spieler in DIESEM Takt nichts mehr.
      // Ob die Figur weiterläuft, entscheidet allein der Auto-Lauf in der
      // Simulation. Hier wird nichts gemerkt.
      this.#daumen = null;
    };
    f.addEventListener('pointerup', loslassen);
    f.addEventListener('pointercancel', loslassen);

    f.addEventListener(
      'wheel',
      (e) => {
        e.preventDefault();
        this.#beiZoom?.(e.deltaY > 0);
      },
      { passive: false },
    );

    window.addEventListener('keydown', (e) => {
      if (e.repeat) return;
      const taste = e.key.toLowerCase();
      this.#tasten.add(taste);
      if (taste === 'r') this.umschaltenAutolauf();
      if (taste === 'q') this.#wartende.push({ art: 'anker', an: true });
      if (e.key === ' ') {
        e.preventDefault();
        this.umschaltenAnker();
      }
      const nummer = Number.parseInt(e.key, 10);
      if (nummer >= 1 && nummer <= 5) {
        this.#wartende.push({ art: 'faehigkeit', slot: nummer - 1, ziel: null });
      }
    });
    window.addEventListener('keyup', (e) => this.#tasten.delete(e.key.toLowerCase()));
    window.addEventListener('blur', () => this.#tasten.clear());
  }

  #autolauf = false;
  #anker = false;

  umschaltenAutolauf(): void {
    this.setzeAutolauf(!this.#autolauf);
  }

  setzeAutolauf(an: boolean): void {
    this.#autolauf = an;
    this.#wartende.push({ art: 'autolauf', an });
  }

  umschaltenAnker(): void {
    this.setzeAnker(!this.#anker);
  }

  setzeAnker(an: boolean): void {
    this.#anker = an;
    this.#wartende.push({ art: 'anker', an });
  }

  /** Spiegelt den Zustand aus dem Schnappschuss zurück — die Sim führt ihn. */
  uebernimm(autolauf: boolean, anker: boolean): void {
    this.#autolauf = autolauf;
    this.#anker = anker;
  }

  loeseAus(slot: number): void {
    this.#wartende.push({ art: 'faehigkeit', slot, ziel: null });
  }

  setzeHandbetrieb(slot: number, an: boolean): void {
    this.#wartende.push({ art: 'handbetrieb', slot, an });
  }

  setzeAutomatikgrad(grad: Automatikgrad): void {
    this.#wartende.push({ art: 'automatikgrad', grad });
  }

  setzeTempoprofil(profil: string): void {
    this.#wartende.push({ art: 'tempoprofil', profil });
  }

  hebeAuf(id: number): void {
    this.#wartende.push({ art: 'aufheben', id });
  }

  steigeEin(): void {
    this.#wartende.push({ art: 'einsteigen' });
  }

  waehleFaehigkeit(faehigkeit: string): void {
    this.#wartende.push({ art: 'waehleFaehigkeit', faehigkeit });
  }

  #tastenRichtung(): Vek | null {
    let x = 0;
    let y = 0;
    if (this.#tasten.has('a') || this.#tasten.has('arrowleft')) x -= 1;
    if (this.#tasten.has('d') || this.#tasten.has('arrowright')) x += 1;
    if (this.#tasten.has('w') || this.#tasten.has('arrowup')) y -= 1;
    if (this.#tasten.has('s') || this.#tasten.has('arrowdown')) y += 1;
    if (x === 0 && y === 0) return null;
    const l = Math.hypot(x, y);
    return { x: x / l, y: y / l };
  }

  /**
   * Absichten für den nächsten Simulationstakt. Leert die Warteschlange.
   *
   * `richtung` ist genau das, was der Spieler JETZT will — Tastatur oder
   * Daumen, sonst nichts. Kein gemerkter Wert, kein zweiter Wahrheitsstand.
   */
  absichten(): Absicht[] {
    const aus = this.#wartende;
    this.#wartende = [];
    aus.push({ art: 'bewegen', richtung: this.#tastenRichtung() ?? this.#daumen });
    return aus;
  }
}
